import React, { useEffect } from 'react'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import { makeStyles } from '@mui/styles'
import { Portfolio, Position } from '../../redux/reducers/portfolio'
import { Divider, Typography } from '@mui/material'
import { newThemeObject } from '../../newTheme'
import { useTranslation } from 'react-i18next'
import { Security } from '../../redux/reducers/securities'
import { TransparentButton } from '../common/TransparentButton'
import { fixAdress } from '../../utils/webpage'
import { hexToRGBA } from '../../utils/styles'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { formatNumber } from '../../utils/numbersFormatter'
import classNames from 'classnames'
import { InstrumentIcon } from '../common/InstrumentIcon'
import { useClientApi } from '../../hooks/useClientApi'
import { ClientPortfolioType } from '../../redux/reducers/clients'
import { format } from 'date-fns'

const useStyles = makeStyles(theme => ({
  detailsRow: {
    '& .MuiTableCell-root': {
      borderBottom: 'none',
      paddingTop: 0,
      paddingBottom: theme.spacing(2),
      background: `linear-gradient(90deg,
            ${hexToRGBA(newThemeObject.palette.main.highlight, 0)} 0%,
            ${hexToRGBA(newThemeObject.palette.main.highlight, 1)} 1%,
            ${hexToRGBA(newThemeObject.palette.main.highlight, 1)} 99%,
            ${hexToRGBA(newThemeObject.palette.main.highlight, 0)} 100%)`,
      height: 0,
    },
  },
  detailsTitle: {
    fontWeight: 600,
  },
  marketInformationTable: {
    display: 'grid',
    gridTemplateColumns: `1fr 1fr `,
    gridTemplateRows: '1fr 1px 1fr 1px 1fr',
    columnGap: theme.spacing(6),
  },
  marketInformationTableLine: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  MITitle: {
    color: newThemeObject.palette.main.sectext,
  },
  MIValue: {
    fontWeight: 600,
  },
  wrapper: {
    display: 'grid',
    gridTemplateColumns: '3fr 4fr 2fr',
    gap: theme.spacing(2),
  },
  comment: {
    color: newThemeObject.palette.main.sectext,
    whiteSpace: 'pre-wrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordWrap: 'break-word',
    maxHeight: '3rem',
    overflowY: 'auto',
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
  },
  button: {
    backgroundColor: hexToRGBA(newThemeObject.palette.main.dark, 0.05),
    color: newThemeObject.palette.main.dark,
    height: '2rem',
    borderRadius: '1rem',
    padding: '0rem 1rem',
  },

  table: {
    minWidth: 1000,
  },
  tableHead: {
    ...newThemeObject.table.tableHead, 
  },
  tableHead2: {
    '& .MuiTableCell-root': {
      background: '#E8ECF3',
      margin: '10px 0 0 10px',
      height: '2rem',
      padding: '0',
      borderBottom: 'none',
    },
  },
  tableHeadChildrenText: {
    ...newThemeObject.table.tableHeadChildrenText,
  },
  tableHeadChildren: {
    ...newThemeObject.table.tableHeadChildren,
  },
  tableHeadChildrenFirst: {
    ...newThemeObject.table.tableHeadChildrenFirst,
  },
  tableHeadChildrenLast: {
    ...newThemeObject.table.tableHeadChildrenLast,
  },
  tableContent: {
    ...newThemeObject.table.tableContent,
  },
  tableContentFirst: {
    ...newThemeObject.table.tableContentFirst,
  },
  clickableRow: {
    '& .MuiTableCell-root': {
      borderBottom: 'none',
      borderTop: 'thin solid',
      borderColor: 'rgba(0, 0, 0, 0.12)',
      paddingTop: '16px',
    },
  },
  firstClickableRow: {
    '& .MuiTableCell-root': {
      borderBottom: 'none',
      paddingTop: '16px',
    },
  },
  text: {
    fontWeight: '400',
    margin: '0',
    fontSize: '0.7rem',
    textWrap: 'wrap',
  },

}))

interface DataTableRowDetailsProps {
  position: Position
  portfolio: ClientPortfolioType | null
  getValidSecurity: (securityId: number) => Security | null
}

const DataTableRowDetails: React.FC<DataTableRowDetailsProps> = ({ position, getValidSecurity, portfolio }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  // const buys = useSelector(state => state.detailPortfolioState.portfolio.buys)
  const { getPositionBuys } = useClientApi()

  useEffect(()=>{
    console.log('position', position);
    if (position && position.positionId) {
      getPositionBuys(position.positionId)
    }
  }, [position])

  // useEffect(()=>{
  //   console.log('buys', buys)
  // }, [buys])

  const handleDownloadKeyInfo = () => {
    const security = getValidSecurity(position.instrument.instrumentId)
    if (!security) return

    const linkSource = `${security.fileKeyMessageHeader},${security.fileKeyMessageContent}`
    const downloadElement = document.createElement('a')
    const fileName = security.fileKeyMessageName
    downloadElement.href = linkSource
    downloadElement.download = fileName || 'empty_name.pdf'
    downloadElement.click()
  }

  const handleDownloadProspect = () => {
    const security = getValidSecurity(position.instrument.instrumentId)
    if (!security) return

    const linkSource = `${security.fileProspectHeader},${security.fileProspectContent}`
    const downloadElement = document.createElement('a')
    const fileName = security.fileProspectName
    downloadElement.href = linkSource
    downloadElement.download = fileName || 'empty_name.pdf'
    downloadElement.click()
  }

  const security = getValidSecurity(position.instrument.instrumentId)

  return (
    <TableRow className={classes.detailsRow}>
      <TableCell colSpan={14}>
        <div className={classes.wrapper}>
          <span>
            <p className={classes.detailsTitle}>{t('MARKET_INFORMATION').toUpperCase()}</p>
            <div className={classes.marketInformationTable}>
              <div className={classes.marketInformationTableLine}>
                <p className={classes.MITitle}>{t('HORIZONTE')}</p>
                <p className={classes.MIValue}>{security?.horizonteValue}</p>
              </div>
              <div className={classes.marketInformationTableLine}>
                <p className={classes.MITitle}>{t('EXPERIENCE')}</p>
                <p className={classes.MIValue}>{t(security?.skillAndExperience || '')}</p>
              </div>
              <Divider variant="middle" style={{ gridColumn: 'span 2' }} />
              <div className={classes.marketInformationTableLine}>
                <p className={classes.MITitle}>{t('RISK')}</p>
                <p className={classes.MIValue}>{security?.riskValue}</p>
              </div>
              <div className={classes.marketInformationTableLine}>
                <p className={classes.MITitle}>{t('PREFERENCE')}</p>
                <p className={classes.MIValue}>{t(security?.preference || '')}</p>
              </div>
              <Divider variant="middle" style={{ gridColumn: 'span 2' }} />
              <div className={classes.marketInformationTableLine}>
                <p className={classes.MITitle}>{t('CATEGORY')}</p>
                <p className={classes.MIValue}>{t(security?.category || '')}</p>
              </div>

              <div className={classes.marketInformationTableLine}>
                <p className={classes.MITitle}>{t('RISK3')}</p>
                <p className={classes.MIValue}>{t(security?.lossAbility || '')}</p>
              </div>
            </div>
          </span>
          <span>
            <p className={classes.detailsTitle}>{t('SECURITY_COMMENT').toUpperCase()}</p>
            <p className={classes.comment}>{security?.comment}</p>
          </span>
          <span>
            <p className={classes.detailsTitle}>{t('ADDITIONAL_INFORMATION').toUpperCase()}</p>
            <Typography className={classes.comment}>{security?.additionalInformation}</Typography>
          </span>
        </div>
        <div className={classes.buttons}>
          <TransparentButton
            onClick={handleDownloadProspect}
            disabled={!security?.fileProspectHeader && !security?.fileProspectContent}
            startIcon={<DownloadRoundedIcon />}
            className={classes.button}
          >
            {t('DOWNLOAD_PROSPECT')}
          </TransparentButton>

          <TransparentButton
            onClick={handleDownloadKeyInfo}
            disabled={!security?.fileKeyMessageHeader && !security?.fileKeyMessageContent}
            startIcon={<DownloadRoundedIcon />}
            className={classes.button}
          >
            {t('DOWNLOAD_KEY_MESSAGE')}
          </TransparentButton>
          <a href={fixAdress(security?.webpage?.toString())} target="blank">
            <TransparentButton disabled={!security?.webpage} startIcon={<LinkRoundedIcon />} className={classes.button}>
              {t('URL_COMPANY')}
            </TransparentButton>
          </a>
        </div>
        <div style={{borderTop: '1px solid #48494B66', marginTop: '10px'}}>
          <span>
            <p className={classes.detailsTitle}>{t('BUYS').toUpperCase()}</p>
            { portfolio && portfolio.buys && portfolio.buys.length>0 && portfolio.buys.filter((i)=>i.positionId === position.positionId).length>0 &&

              <Table className={classes.table} size="small">
                <TableHead className={classes.tableHead2}>
                  <TableRow>
                    <TableCell className={classNames([classes.tableHeadChildren, classes.tableHeadChildrenFirst])} />
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('DATE')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('ISIN')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('NAME')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('TYPE')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('ORDER_QUANTITY')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('PURCHASE_PRICE')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('LAST_PRICE')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('P_L')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('INVESMENT_AMOUNT')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>{t('CURRENCY')}</p>
                    </TableCell>
                    <TableCell className={classes.tableHeadChildren}>
                      <p className={classes.tableHeadChildrenText}>
                        {t('INVESMENT_AMOUNT_IN')} {portfolio.currency}
                      </p>
                    </TableCell>                    
                  </TableRow>
                </TableHead>
                <TableBody>
                  {portfolio && portfolio.buys && portfolio.buys.filter((i)=>i.positionId === position.positionId)[0].buys.map((position2: Position, index: number) => (
                    <React.Fragment key={position2.positionId}>
                      <TableRow
                        className={index === 0 ? classes.firstClickableRow : classes.clickableRow}
                      >
                        <TableCell className={classNames(classes.tableContent, classes.tableContentFirst)}>
                          <InstrumentIcon securityType={position2.instrument.assetClass} />
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{format(new Date(position2.dateFrom), 'dd/MM/yyyy')}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{position2.instrument.isin}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{position2.instrument.name}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{position2.instrument.instrumentType}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{position2.quantity}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{formatNumber(position2.averagePurchasePrice)}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{formatNumber(position2.lastPrice)}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p
                            className={classes.text}
                            style={
                              position2.profitLossPercentage > 0
                                ? { color: newThemeObject.palette.main.green, fontWeight: 700 }
                                : { color: newThemeObject.palette.main.primary, fontWeight: 700 }
                            }
                          >
                            {formatNumber(position2.profitLoss) + ' ('+formatNumber(position2.profitLossPercentage)+'%)'}
                          </p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{formatNumber(position2.amount)}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{position2.instrument.currency}</p>
                        </TableCell>
                        <TableCell className={classes.tableContent}>
                          <p className={classes.text}>{formatNumber(position2.portfolioCurrencyAmount)}</p>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>

            }

          </span>
        </div>
      </TableCell>
    </TableRow>
  )
}

export default DataTableRowDetails
